import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import {
  ArrowRight, CheckCircle2, ArrowLeft, Phone,
  Briefcase, GraduationCap, Home, Plane, TrendingUp, BookOpen, Languages, MessageCircle
} from "lucide-react";

const iconMap = {
  Briefcase, GraduationCap, Home, Plane, TrendingUp, BookOpen, Languages, MessageCircle
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getService(slug)
      .then(data => { setService(data); setLoading(false); })
      .catch(() => { setService(null); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Service Not Found</h1>
        <p className="text-gray-500 mb-6">The service you're looking for doesn't exist.</p>
        <Link to="/">
          <Button className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Briefcase;

  return (
    <div>
      <SEO 
        title={`${service.title} - Immigration Services`}
        description={service.description.substring(0, 160)}
        keywords={`${service.title}, immigration, visa services, ${service.slug}, NexFLY`}
        image={service.image}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 bg-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[50%] h-full bg-brand-50/60 rounded-bl-[100px]" />
          <div className="absolute bottom-10 left-10 w-48 h-48 bg-brand-100/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-sm text-brand-500 hover:text-brand-700 font-medium mb-8 group">
            <ArrowLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-14 h-14 bg-brand-100 rounded-2xl flex items-center justify-center mb-6">
                <IconComponent className="w-7 h-7 text-brand-500" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
                {service.title}
              </h1>
              <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-lg">
                {service.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="tel:+918248323674">
                  <Button className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl h-12 px-7 text-sm font-semibold shadow-lg shadow-brand-200/60 hover:shadow-brand-300/60 transition-all duration-300 hover:-translate-y-0.5">
                    <Phone className="w-4 h-4 mr-2" />
                    Enquire Now
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="border-2 border-brand-200 text-brand-700 hover:bg-brand-50 rounded-xl h-12 px-7 text-sm font-semibold transition-all duration-300">
                    Book Consultation
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-brand-100/40">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              What We Offer
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Key Features & Benefits
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-brand-500" />
                  </div>
                  <p className="text-gray-700 text-sm font-medium leading-relaxed">{feature}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              Our Process
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              How It Works
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.process.map((step, index) => (
              <div key={index} className="relative">
                <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group h-full">
                  <CardContent className="p-7">
                    <div className="w-12 h-12 bg-brand-800 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                      <span className="text-white font-bold text-lg">{step.step}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-base mb-2">{step.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
                {index < service.process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-5 h-5 text-brand-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-20 bg-brand-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
                Requirements
              </Badge>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Eligibility Criteria
              </h2>
              <div className="space-y-4">
                {service.eligibility.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 group">
                    <div className="w-6 h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-brand-500 transition-colors duration-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-brand-500 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:block hidden">
              <div className="bg-white rounded-3xl p-10 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Not Sure If You Qualify?</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  Our experts can evaluate your profile and advise on the best pathway. Book a free consultation to get started.
                </p>
                <Link to="/contact">
                  <Button className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl h-11 px-6 text-sm font-semibold shadow-lg shadow-brand-200/60 transition-all duration-300 hover:-translate-y-0.5">
                    Get Free Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-4 px-4 py-1 text-xs font-semibold rounded-full">
              FAQs
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {service.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="border border-gray-100 rounded-2xl px-6 data-[state=open]:shadow-md data-[state=open]:border-brand-100 transition-all duration-300">
                <AccordionTrigger className="text-left text-sm font-semibold text-gray-800 hover:text-brand-500 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-500 text-sm leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-800 rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-700/30 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-900/30 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-brand-200 text-lg mb-8 max-w-xl mx-auto">
                Take the first step towards your {service.title.toLowerCase()} journey. Our experts are here to help.
              </p>
              <Link to="/contact">
                <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-xl h-12 px-8 text-sm font-semibold shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                  Contact Us Today
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
